import type { BoxProps } from '@chakra-ui/react';
import { Box } from '@chakra-ui/react';
import { CardCarousel } from '@time-line/components/common';
import { useEffect, useState } from 'react';
import type { PromoCode } from 'types/cart/PromoCode';
import type { ProductComparison } from 'types/cms/blocks';
import type { Table } from 'types/cms/Table';

import { getDividerStyles } from '../utils/getDividerStyles';
import { getTallestRowHeights } from '../utils/getTallestRowHeights';
import { CarouselItem } from './CarouselItem';

interface CarouselProps extends BoxProps {
  table: Table;
  products: ProductComparison['products'];
  promoCode?: PromoCode;
  ctaType: ProductComparison['ctaType'];
  imageType: ProductComparison['imageType'];
}

export type RowHeights = (number | undefined)[];
export type ProductRowHeights = Record<string, RowHeights>;

export function Carousel({
  products,
  table,
  promoCode,
  ctaType,
  imageType,
  ...props
}: CarouselProps) {
  const [computedRowHeights, setComputedRowHeights] = useState<RowHeights>();
  const [rowHeights, setRowHeights] = useState<ProductRowHeights>({});

  const getRowHeights = (
    productSlug: string,
    currentRowHeights: RowHeights,
  ) => {
    setRowHeights((prev) => ({
      ...prev,
      [productSlug]: currentRowHeights,
    }));
  };

  useEffect(() => {
    const tallestHeights = getTallestRowHeights(rowHeights, products.length);

    if (tallestHeights) {
      setComputedRowHeights(tallestHeights);
    }
  }, [rowHeights, products.length]);

  return (
    <Box pos="relative" {...props}>
      <CardCarousel
        my={0}
        swiperBreakpoints={{
          320: {
            slidesPerView: 2.15,
            spaceBetween: 12,
          },
        }}
      >
        {products.map((product, productIndex) => {
          const { slug } = product;
          const productData = table.rows.map(
            (row) => row.cells[productIndex + 1],
          );
          const rowHeadings = table.rows.map((row) => row.cells[0]);
          const isNotLastProduct = productIndex < products.length - 1;

          return (
            <CarouselItem
              key={slug}
              productsCount={products.length}
              productData={productData}
              rowHeadings={rowHeadings}
              rowProps={getDividerStyles(isNotLastProduct)}
              computedRowHeights={computedRowHeights}
              product={product}
              getRowHeights={(heights) => getRowHeights(slug, heights)}
              promoCode={promoCode}
              ctaType={ctaType}
              imageType={imageType}
            />
          );
        })}
      </CardCarousel>
    </Box>
  );
}
